import fetcher from '@/services/fetcher'
import { InstantWinStorage } from '@/services/instant-win-storage'
import { PaymentType } from '@/utils/helper-objects'
import { toApiUrl } from '@/utils/url-generation-utils'
import { useGtm } from '@gtm-support/vue-gtm'
import { ref } from 'vue'
import type { Ref } from 'vue'

/** Successful response we get back from the Instant Win charge endpoint.  */
export interface InstantWinChargeSuccess {
  status: 'success'
  // Include the fields your API actually returns, or keep this index signature:
  [key: string]: unknown
}

export type SubscriptionSuccess = { success: true; redirectUrl: string }
export type PaymentUpdateSuccess = { success: true }
export type PaymentFailure = { success: false; error: unknown }

/**
 * Union of all possible results from handlePaymentAuthorized().
 * If your current implementation can fall through without returning,
 * add `| undefined` to this union; otherwise keep it tight.
 */
export type HandlePaymentAuthorizedReturn =
  | SubscriptionSuccess
  | PaymentUpdateSuccess
  | InstantWinChargeSuccess
  | PaymentFailure

/** The public surface returned by useChargebee(). */
export type UseChargebeeReturn = {
  /** Mount or initialize any Chargebee UI / logic. */
  mount: () => Promise<{ paymentIntentData: any } | void>

  /** Reactive error message shown to the user (null = no error). */
  errors: Ref<string | null>

  /**
   * Authorize and submit payment based on checkoutData.paymentType.
   * Returns a discriminated success or a failure with an error.
   */
  handlePaymentAuthorized: (
    authorizedPayment: unknown,
    checkoutData: unknown
  ) => Promise<HandlePaymentAuthorizedReturn>

  /** Map and surface an error to the UI. */
  handlePaymentError: (error: Error) => void

  /** Reactive loading flag while requests are in-flight. */
  isLoading: Ref<boolean>
}

let loadPromise: Promise<void> | null = null

async function loadChargebeeScript(): Promise<void> {
  // Reset the promise if window.Chargebee is undefined
  if (typeof window.Chargebee === 'undefined') {
    loadPromise = null
  }

  if (loadPromise) {
    return loadPromise
  }

  loadPromise = new Promise((resolve, reject) => {
    const site = import.meta.env.GLP_CHARGEBEE_SITE
    const publishableKey = import.meta.env.GLP_CHARGEBEE_PUBLISHABLE_KEY
    // Check if script already exists and is initialized
    if (typeof window.Chargebee !== 'undefined') {
      try {
        window.Chargebee.getInstance()
        resolve()
        return
      } catch {
        // Need to initialize
        try {
          void window.Chargebee.init({
            site,
            publishableKey,
          }).then(() => {
            console.log('Chargebee initialized successfully')
            resolve()
          })
          return
        } catch (error: unknown) {
          reject(error)
          return
        }
      }
    }

    // Script doesn't exist, create and load it
    const script = document.createElement('script')
    script.src = 'https://js.chargebee.com/v2/chargebee.js'
    script.async = true

    script.onload = () => {
      try {
        // Initialize Chargebee synchronously after script loads
        window.Chargebee.init({
          site,
          publishableKey,
        })
        console.log('Chargebee initialized successfully')
        resolve()
      } catch (error: unknown) {
        console.error('Failed to initialize Chargebee:', error)
        loadPromise = null
        reject(error)
      }
    }

    script.onerror = (error) => {
      console.error('Failed to load Chargebee script:', error)
      loadPromise = null
      reject(new Error('Failed to load Chargebee script'))
    }

    document.head.appendChild(script)
  })

  return loadPromise
}

export function useChargebee(): UseChargebeeReturn {
  const gtm = useGtm()
  const chargebeeErrors = ref<string | null>(null)
  const isLoading = ref(false)

  async function mount(): Promise<{ paymentIntentData: any } | void> {
    try {
      // Load Chargebee script first
      await loadChargebeeScript()
    } catch (error) {
      console.error('Chargebee mount error:', error)
      chargebeeErrors.value =
        error instanceof Error ? error.message : 'Failed to load payment system'
      throw error
    }
  }

  async function handlePaymentAuthorized(
    authorizedPayment: any,
    checkoutData: any
  ) {
    console.log('handlePaymentAuthorized', authorizedPayment, checkoutData)
    const redirectPage = '/thank-you'
    isLoading.value = true
    try {
      const site = import.meta.env.GLP_CHARGEBEE_SITE

      // It's a subscription payment
      if (checkoutData?.paymentType === PaymentType.Subscription) {
        const subscriptionData = {
          ...checkoutData,
          plan_code: checkoutData.plan.code,
          paymentIntentId: authorizedPayment.id,
          payment: authorizedPayment,
          line1: checkoutData.address_line_1,
          line2: checkoutData.address_line_2,
          postcode: checkoutData.post_code,
          card_type: 'card',
          site,
          consent: checkoutData.consent?.toString(),
          success_url: `${window.location.origin}${redirectPage}`,
        }
        const redirectUrl = await submitPayment({
          ...subscriptionData,
        })

        if (!localStorage.getItem('show-subscription-onboarding')) {
          localStorage.setItem('show-subscription-onboarding', 'true')
        }

        return { success: true, redirectUrl }
        // It's an update to an existing payment method
      } else if (checkoutData?.paymentType === PaymentType.PaymentUpdate) {
        const paymentUpdateData = {
          payment_intent_id: authorizedPayment.id,
          customer_id: checkoutData.id,
          site,
        }
        await submitPaymentUpdate({
          ...paymentUpdateData,
        })
        return { success: true }
        // It's a one-time payment for an instant win
      } else if (checkoutData?.paymentType === PaymentType.InstantWin) {
        const instantWinData = {
          ...checkoutData,
          amount: authorizedPayment.amount,
          paymentIntentId: authorizedPayment.id,
          payment: authorizedPayment,
          line1: checkoutData.address_line_1,
          line2: checkoutData.address_line_2,
          postcode: checkoutData.post_code,
          card_type: 'card',
          site,
          consent: checkoutData.consent?.toString(),
        }
        console.log('submitting payment for instant win', instantWinData)
        const response = await submitPaymentInstantWin({
          ...instantWinData,
        })

        if (!localStorage.getItem('show-instant-wins-onboarding')) {
          localStorage.setItem('show-instant-wins-onboarding', 'true')
        }

        return response
      }
    } catch (e) {
      handlePaymentError(e as Error)
      return { success: false, error: e }
    } finally {
      isLoading.value = false
    }
  }

  function handlePaymentError(error: Error) {
    chargebeeErrors.value = error.message || 'Payment failed'
    isLoading.value = false
  }

  async function submitPayment(paymentData: any) {
    try {
      const { data, error } = await fetcher.post(
        toApiUrl('create_subscription'),
        {
          ...paymentData,
          // Default UTM parameters, will be overwritten if provided
          utm_source: '',
          utm_medium: '',
          utm_campaign: '',
        },
        true
      )

      if (
        data.value.status === 'success' &&
        typeof data.value.redirect === 'string'
      ) {
        return data.value.redirect
      }

      if (error.value instanceof Error) {
        throw error.value
      } else {
        throw new Error(error.value.message || 'Failed to create subscription')
      }
    } catch (e: any) {
      gtm?.trackEvent({
        event: 'checkout_error',
        checkout_error_code: 'chargebee',
        checkout_error_message: e.message,
      })

      chargebeeErrors.value = e.message

      throw e
    }
  }

  async function submitPaymentUpdate(paymentData: any) {
    try {
      const { data } = await fetcher.post(
        toApiUrl('payments/add_payment_method'),
        {
          ...paymentData,
        },
        true
      )

      if (data.value?.status === 'success') {
        return true
      }

      throw new Error(data.value?.message || 'Updating payment method failed')
    } catch (e: any) {
      gtm?.trackEvent({
        event: 'checkout_error',
        checkout_error_code: 'chargebee',
        checkout_error_message: e.message,
      })

      chargebeeErrors.value = e.message
      throw e
    }
  }

  async function submitPaymentInstantWin(paymentData: any) {
    console.log('submitPaymentOneTime', paymentData)
    try {
      const { data, error } = await fetcher.post(
        toApiUrl('instantwin/charge'),
        {
          ...paymentData,
        },
        true
      )

      if (data.value.status === 'success') {
        // Clear the reservation after successful purchase
        InstantWinStorage.removeReservation(paymentData.drawCode)
        return data.value
      }

      if (error.value instanceof Error) {
        throw error.value
      } else {
        const errorMessage = data.value?.message || 'Payment failed'
        throw new Error(errorMessage)
      }
    } catch (e: any) {
      gtm?.trackEvent({
        event: 'checkout_error',
        checkout_error_code: 'chargebee',
        checkout_error_message: e.message,
      })

      chargebeeErrors.value = e.message
      throw e
    }
  }

  return {
    mount,
    errors: chargebeeErrors,
    handlePaymentAuthorized,
    handlePaymentError,
    isLoading,
  }
}
